import { type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { ChevronDown } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

export type Props = {
  /** Main title text */
  title: string;
  /** Subtitle content below title */
  subtitle?: ReactNode;
  /** Optional icon element to the left of title */
  icon?: ReactNode;
  /** Action buttons shown on the right side of the header */
  actions?: ReactNode;
  /** Whether the card is expandable and currently expanded */
  isExpanded?: boolean;
  /** Toggle expand/collapse callback */
  onToggle?: () => void;
  /** Content shown when expanded */
  children?: ReactNode;
};

/**
 * Reusable bordered card with optional expand/collapse.
 * Extracted from the SurveyItem pattern.
 */
export function ActionCard({
  title,
  subtitle,
  icon,
  actions,
  isExpanded,
  onToggle,
  children,
}: Props) {
  const isExpandable = onToggle !== undefined;

  const header = (
    <>
      {icon && <div className="shrink-0">{icon}</div>}

      <div className="flex flex-col items-start gap-1 flex-1 min-w-0">
        <Type variant={isExpanded ? "h4" : "h5"} className="truncate w-full text-left">
          {title}
        </Type>
        {subtitle &&
          (typeof subtitle === "string" ? (
            <Type variant="caption" className="text-brand-800">
              {subtitle}
            </Type>
          ) : (
            subtitle
          ))}
      </div>

      {(actions || isExpandable) && (
        <div className="flex items-center gap-3 shrink-0">
          {actions}
          {isExpandable && (
            <Button
              as="span"
              variant="ghost"
              iconComponent={ChevronDown}
              tabIndex={-1}
              className={twMerge(
                "transition-transform duration-200",
                isExpanded && "rotate-180",
              )}
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <div
      className={twMerge(
        "w-full border border-brand-800 rounded-lg overflow-hidden transition-all duration-200",
        isExpanded && "shadow-sm",
      )}
    >
      {isExpandable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className={twMerge(
            "w-full flex items-center gap-4 px-5 py-4 transition-colors cursor-pointer rounded-lg",
            "hover:bg-brand-50",
            isExpanded && "rounded-b-none",
          )}
        >
          {header}
        </button>
      ) : (
        <div className="flex items-center gap-4 px-5 py-4">{header}</div>
      )}

      {isExpanded && children && <div>{children}</div>}
    </div>
  );
}

export default ActionCard;
